import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { WatchItem, getWatchlist, addWatch, removeWatch, watchKey } from '../services/watchlist.service';

/**
 * Loads the signed-in bidder's watchlist once and exposes an optimistic toggle.
 * Use it high in a page (e.g. SalePage) and pass isWatched/toggle down to each lot,
 * so the whole grid shares ONE read instead of one per card.
 */
export function useWatchlist() {
  const { currentUser } = useAuth();
  const [watched, setWatched] = useState<Record<string, WatchItem>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!currentUser) { setWatched({}); setLoading(false); return; }
      try {
        const map = await getWatchlist(currentUser.uid);
        if (active) setWatched(map);
      } catch (e) {
        console.error('Failed to load watchlist:', e);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [currentUser]);

  const isWatched = useCallback(
    (auctionId: string, lotId: string) => !!watched[watchKey(auctionId, lotId)],
    [watched],
  );

  const toggle = useCallback(async (item: WatchItem) => {
    if (!currentUser) {
      toast('Sign in to build your watchlist', { icon: '👀' });
      return;
    }
    const key = watchKey(item.auctionId, item.lotId);
    const already = !!watched[key];
    const entry: WatchItem = { ...item, addedAt: item.addedAt || Date.now() };

    // Optimistic update.
    setWatched((w) => {
      const n = { ...w };
      if (already) delete n[key]; else n[key] = entry;
      return n;
    });

    try {
      if (already) await removeWatch(currentUser.uid, item.auctionId, item.lotId);
      else await addWatch(currentUser.uid, entry);
    } catch (e) {
      console.error('Watch toggle failed:', e);
      toast.error('Could not update your watchlist');
      // Roll back.
      setWatched((w) => {
        const n = { ...w };
        if (already) n[key] = entry; else delete n[key];
        return n;
      });
    }
  }, [currentUser, watched]);

  return { watched, isWatched, toggle, loading };
}
